import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PublicRoutes from './public/publicRoutes';
import Layouts from '../Layout';
import { WizardOnboardingLock } from '../components/ui/ProtectedRoutes';

const PUBLIC_PATHS = [
  '/',
  '/login',
  '/sign-up',
  '/signup',
  '/terms-and-conditions',
  '/legal/terms',
  '/privacy-policy',
  '/legal/privacy',
  '/contact-us',
  '/contact',
  '/mfa-verification',
  '/faq',
  '/forgot-password',
  '/pricing',
  // Public discovery. Invite, apply, and dashboards stay off this list.
  '/creator-map',
  '/marketplace/influencers',
  '/influencers',
  '/marketplace',
  // Onboarding must bypass RequireSession: App.js sets isAuthenticated on a short
  // delay after userLoggedIn, but MFA/signup navigates here immediately with a token
  // already in localStorage. ProtectedRoute still enforces JWT + user type.
  '/brand-onboarding',
  '/influencer-onboarding',
];

const PUBLIC_PATH_PREFIXES = [
  '/blog',
  '/reset-password/',
];

function isGuestDiscoveryPath(pathname) {
  if (pathname === '/marketplace/campaigns') return true;
  if (/^\/marketplace\/influencers\/[^/]+\/dp\/[^/]+$/.test(pathname)) return true;
  if (/^\/marketplace\/influencers\/dp\/[^/]+$/.test(pathname)) return true;
  if (/^\/marketplace\/influencers\/[^/]+$/.test(pathname)) return true;
  if (/^\/marketplace\/campaigns\/[^/]+\/dp\/[^/]+$/.test(pathname)) return true;
  if (/^\/marketplace\/campaigns\/dp\/[^/]+$/.test(pathname)) return true;
  if (/^\/marketplace\/campaigns\/[^/]+$/.test(pathname)) return true;
  return false;
}

function isPublicPath(pathname) {
  return PUBLIC_PATHS.includes(pathname)
    || PUBLIC_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
    || isGuestDiscoveryPath(pathname);
}

function isOnboardingPath(pathname) {
  return pathname === '/brand-onboarding' || pathname === '/influencer-onboarding';
}

function shouldLoadRoleRoutes(isAuthenticated, pathname) {
  return isAuthenticated || isOnboardingPath(pathname) || !isPublicPath(pathname);
}

function hasOnboardingRoute(pathname, brandRoutes, influencerRoutes) {
  if (pathname === '/brand-onboarding') return brandRoutes.length > 0;
  if (pathname === '/influencer-onboarding') return influencerRoutes.length > 0;
  return true;
}

function RequireSession({ isAuthenticated, children }) {
  const location = useLocation();

  if (isAuthenticated || isPublicPath(location.pathname)) {
    return children;
  }

  return <Navigate to="/login" state={{ from: location }} replace />;
}

const scheduleDeferredRouteLoad = (callback) => {
  if (typeof window.requestIdleCallback === 'function') {
    const idleCallbackId = window.requestIdleCallback(callback);
    return {
      cancel: () => window.cancelIdleCallback?.(idleCallbackId),
    };
  }

  const timeoutId = window.setTimeout(callback, 200);
  return { cancel: () => window.clearTimeout(timeoutId) };
};

function AppRoutes({ isAuthenticated, userType }) {
  const location = useLocation();
  const publicRoutes = PublicRoutes(isAuthenticated, userType);
  const [brandRoutes, setBrandRoutes] = useState([]);
  const [influencerRoutes, setInfluencerRoutes] = useState([]);
  const [adminRoutes, setAdminRoutes] = useState([]);
  const [roleRoutesReady, setRoleRoutesReady] = useState(false);

  // Guests on public paths (e.g. /blog) skip loading role route modules.
  // Onboarding is public for RequireSession (MFA navigates before App.js
  // flips isAuthenticated) but the page itself lives in role routes.
  const loadRoleRoutes = shouldLoadRoleRoutes(isAuthenticated, location.pathname);

  useEffect(() => {
    if (!loadRoleRoutes) {
      setBrandRoutes([]);
      setInfluencerRoutes([]);
      setAdminRoutes([]);
      setRoleRoutesReady(true);
      return undefined;
    }

    let cancelled = false;
    let deferredLoad;
    setRoleRoutesReady(false);

    const loadBrandRoutes = () => import('./brand/brandRoutes');
    const loadInfluencerRoutes = () => import('./influencer/influencerRoutes');
    const loadAdminRoutes = () => import('./admin/adminRoutes');
    const setLoadedRoutes = (role, module) => {
      if (role === 'brand') {
        setBrandRoutes(module.default());
      } else if (role === 'influencer') {
        setInfluencerRoutes(module.default());
      } else {
        setAdminRoutes(module.default());
      }
    };

    if (userType === 'admin') {
      loadAdminRoutes().then((adminModule) => {
        if (cancelled) return;
        setLoadedRoutes('admin', adminModule);
        setRoleRoutesReady(true);
      });
    } else if (userType === 'brand' || userType === 'influencer') {
      const activeLoader = userType === 'brand' ? loadBrandRoutes : loadInfluencerRoutes;
      const inactiveRole = userType === 'brand' ? 'influencer' : 'brand';
      const inactiveLoader = inactiveRole === 'brand' ? loadBrandRoutes : loadInfluencerRoutes;

      activeLoader().then((activeModule) => {
        if (cancelled) return;
        setLoadedRoutes(userType, activeModule);
        setRoleRoutesReady(true);

        deferredLoad = scheduleDeferredRouteLoad(() => {
          inactiveLoader().then((inactiveModule) => {
            if (!cancelled) setLoadedRoutes(inactiveRole, inactiveModule);
          });
        });
      });
    } else {
      Promise.all([loadBrandRoutes(), loadInfluencerRoutes()])
        .then(([brandModule, influencerModule]) => {
          if (cancelled) return;
          setLoadedRoutes('brand', brandModule);
          setLoadedRoutes('influencer', influencerModule);
          setRoleRoutesReady(true);
        });
    }

    return () => {
      cancelled = true;
      deferredLoad?.cancel();
    };
  }, [loadRoleRoutes, userType]);

  const renderRoutes = (routes) => routes.map(({
    id, path, element, layout, children,
  }) => (
    <Route
      key={id || path}
      path={path}
      element={
        layout ? (
          <Layouts pageType={layout}>{element}</Layouts>
        ) : (
          element
        )
      }
    >
      {children ? renderRoutes(children) : null}
    </Route>
  ));

  // MFA lives on a public path that marks role routes "ready" while they are
  // empty. After verify, wait until the onboarding module is actually loaded.
  const waitingForRoleRoutes = loadRoleRoutes
    && (
      !roleRoutesReady
      || !hasOnboardingRoute(location.pathname, brandRoutes, influencerRoutes)
    );

  if (waitingForRoleRoutes && (isAuthenticated || isOnboardingPath(location.pathname))) {
    return (
      <RequireSession isAuthenticated={isAuthenticated}>
        <WizardOnboardingLock isAuthenticated={isAuthenticated} userType={userType}>
          <div data-testid="role-routes-loading" style={{ minHeight: '100vh' }} />
        </WizardOnboardingLock>
      </RequireSession>
    );
  }

  if (userType === 'admin') {
    return (
      <RequireSession isAuthenticated={isAuthenticated}>
        <WizardOnboardingLock isAuthenticated={isAuthenticated} userType={userType}>
          <Routes>
            {renderRoutes(adminRoutes)}
            <Route path="*" element={<Navigate to="/admin/overview" replace />} />
          </Routes>
        </WizardOnboardingLock>
      </RequireSession>
    );
  }

  return (
    <RequireSession isAuthenticated={isAuthenticated}>
      <WizardOnboardingLock isAuthenticated={isAuthenticated} userType={userType}>
        <Routes>
          {renderRoutes(publicRoutes)}
          {userType === 'influencer' && renderRoutes(influencerRoutes)}
          {userType === 'brand' && renderRoutes(brandRoutes)}
          {userType !== 'influencer' && renderRoutes(influencerRoutes)}
          {userType !== 'brand' && renderRoutes(brandRoutes)}
        </Routes>
      </WizardOnboardingLock>
    </RequireSession>
  );
}

export { isPublicPath, isOnboardingPath, shouldLoadRoleRoutes, hasOnboardingRoute };
export default AppRoutes;
